import type {
  ExpenseCategoryInput, ExpenseInput, ExpenseListItem, Paginated, PaginationInput,
  PurchaseDetail, PurchaseInput, PurchaseListItem, PurchaseStatus,
  SupplierInput, SupplierListItem, SupplierPaymentInput,
} from '@najah/core'
import { api } from './api'

export interface SupplierOption { id: string; code: string; nameAr: string }

export interface SupplierDetail {
  id: string
  code: string
  nameAr: string
  contactName: string | null
  phone: string | null
  email: string | null
  address: string | null
  notes: string | null
  openingBalance: number
  isActive: boolean
  balance: number
}

export interface StatementEntry {
  id: string
  kind: 'purchase' | 'payment'
  reference: string
  detail: string | null
  debit: number
  credit: number
  balance: number
  at: string
}

export interface SupplierStatement {
  supplier: SupplierDetail
  openingBalance: number
  entries: StatementEntry[]
  balance: number
}

export const suppliersApi = {
  list: (query: Partial<PaginationInput>) =>
    api<Paginated<SupplierListItem>>('/suppliers', { query: query as Record<string, string | number | undefined> }),
  options: () => api<SupplierOption[]>('/suppliers/options'),
  get: (id: string) => api<SupplierDetail>(`/suppliers/${id}`),
  statement: (id: string) => api<SupplierStatement>(`/suppliers/${id}/statement`),
  create: (body: SupplierInput) => api<{ id: string }>('/suppliers', { method: 'POST', body }),
  update: (id: string, body: SupplierInput) => api<{ id: string }>(`/suppliers/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<{ success: boolean }>(`/suppliers/${id}`, { method: 'DELETE' }),
}

export interface PurchaseFilter {
  page?: number
  pageSize?: number
  supplierId?: string
  status?: PurchaseStatus
  from?: string
  to?: string
  search?: string
}

export const purchasesApi = {
  list: (filter: PurchaseFilter) =>
    api<Paginated<PurchaseListItem>>('/purchases', { query: filter as Record<string, string | number | undefined> }),
  get: (id: string) => api<PurchaseDetail>(`/purchases/${id}`),
  create: (body: PurchaseInput) =>
    api<{ id: string; reference: string; total: number; status: PurchaseStatus }>('/purchases', {
      method: 'POST',
      body,
    }),
  receive: (id: string) => api<PurchaseDetail>(`/purchases/${id}/receive`, { method: 'POST' }),
  cancel: (id: string) => api<{ success: boolean }>(`/purchases/${id}/cancel`, { method: 'POST' }),
}

export const supplierPaymentsApi = {
  create: (body: SupplierPaymentInput) =>
    api<{ id: string; reference: string; amount: number }>('/supplier-payments', { method: 'POST', body }),
  remove: (id: string) => api<{ success: boolean }>(`/supplier-payments/${id}`, { method: 'DELETE' }),
}

export interface ExpenseCategoryOption { id: string; nameAr: string; icon: string | null; sortOrder: number }

export interface ExpenseFilter {
  page?: number
  pageSize?: number
  categoryId?: string
  from?: string
  to?: string
}

export const expensesApi = {
  list: (filter: ExpenseFilter) =>
    api<Paginated<ExpenseListItem> & { totalAmount: number }>('/expenses', {
      query: filter as Record<string, string | number | undefined>,
    }),
  categories: () => api<ExpenseCategoryOption[]>('/expenses/categories'),
  createCategory: (body: ExpenseCategoryInput) =>
    api<ExpenseCategoryOption>('/expenses/categories', { method: 'POST', body }),
  create: (body: ExpenseInput) =>
    api<{ id: string; reference: string; amount: number }>('/expenses', { method: 'POST', body }),
  remove: (id: string) => api<{ success: boolean }>(`/expenses/${id}`, { method: 'DELETE' }),
}
